import styled from 'styled-components';
import { Checkbox } from 'ui/checkboxV2/Checkbox';
import type { CHECKBOX_STATUS } from 'ui/checkboxV2/types';
import { SortDirections } from 'constants/sortDirections';
import { SORT_DIRECTIONS as SORT_DIRECTIONS_ENUM } from 'constants/sortDirectionsEnum';
import { colors, spacing } from 'ui/theme';
import noop from 'utils/noop';
import { ColumnData } from './constants';
import { getNewSortDirection } from './utils';

import TableHeaderCell, { HeaderCell } from '../tableHeaderCell/TableHeaderCell';

export const StyledHeader = styled.div`
  background: ${colors.white};
  border-color: ${colors.grey15};
  border-style: solid;
  border-width: 0;
  ${({ isSticky }: { isSticky: boolean }) => !isSticky && `border-bottom-width: ${spacing.single};`}
  display: table-row;
  width: 100%;
`;

// header cell matching the table cell padding
// so that the checkbox is aligned with the table rows checkboxes
export const CheckboxHeaderCell = styled(HeaderCell)`
   padding: ${spacing.threeQuarter};
`;

export type TableHeaderProps<SortType extends string = string> = {
  columns: ColumnData[];
  setSortedBy?: (sortedBy: SortType) => void;
  setSortDirection?: (sortDirection: string | SORT_DIRECTIONS_ENUM) => void;
  sortedBy?: SortType;
  sortDirection?: SortDirections | SORT_DIRECTIONS_ENUM;
  isSticky?: boolean;
  /**
   * when this prop is enabled in the `TableRows` component each row in the table
   * starts with a checkbox cell and the defined cells are moved over one column to the right.
   * Enabling `hasCheckboxColumn` in the TableHeader will ensure that the columns are properly aligned.
   * */
  hasCheckboxColumn?: boolean;
  /**
   * `hasCheckbox` works in tandem with `hasCheckboxColumn` here and in the `TableRows` component.
   * If `hasCheckboxColumn` is enabled, `hasCheckbox` will determine whether a checkbox is also rendered in the header.
   */
  hasCheckbox?: boolean;
  /**
   * Callback function that is invoked when the checkbox enabled by `hasCheckbox` is selected.
   */
  onSelect?: () => void;
  checkboxStatus?: CHECKBOX_STATUS;
};

export function TableHeader<SortType extends string = string>({
  columns,
  setSortedBy = noop,
  setSortDirection = noop,
  sortedBy,
  sortDirection = '',
  isSticky = false,
  hasCheckboxColumn = false,
  hasCheckbox = false,
  onSelect = noop,
  checkboxStatus = false,
  ...props
}: TableHeaderProps<SortType>) {
  if (!columns.length) {
    return null;
  }

  return (
    <StyledHeader data-test="table-header-row" role="row" isSticky={isSticky} {...props}>
      {hasCheckboxColumn && (
        <CheckboxHeaderCell
          isSticky={isSticky}
          sortable={false}
          data-test="table-header-rows-have-checkboxes-cell"
        >
          {hasCheckbox && (
            <Checkbox
              data-test="table-header-checkbox"
              forceMountIcon
              label=""
              setChecked={onSelect}
              status={checkboxStatus}
            />
          )}
        </CheckboxHeaderCell>
      )}
      {columns.map((col) => {
        // @ts-ignore
        const { HeaderCell, ...cellProps } = col;
        const id = col.value;
        if (HeaderCell) {
          return <HeaderCell key={id} isSticky={isSticky} {...cellProps} />;
        }
        const isActiveSort = sortedBy === id; // if this column is the active sorting index

        return (
          <TableHeaderCell
            isActiveSort={isActiveSort}
            columnData={col}
            key={id}
            handleClick={() => {
              if (col.sortable === false) return;

              if (isActiveSort) {
                const newSortDirection = getNewSortDirection(sortDirection);
                setSortDirection(newSortDirection);
              } else {
                setSortedBy(id as unknown as SortType);
              }
            }}
            sortable={col.sortable}
            sortDirection={sortDirection}
            isSticky={isSticky}
            width={col.width}
          />
        );
      })}
    </StyledHeader>
  );
}

export default TableHeader;
